/**
 * Calcul des compteurs d'UN contrat sur UNE période de référence —
 * cœur normatif du chapitre 11 (RG-CAL, RG-PRF, RG-PLA, RG-PST, RG-ASS,
 * RG-REP, RG-REC, RG-JRS, RG-HEB, RG-MEN, RG-PAY, RG-FIN).
 *
 * Transcription fidèle du cahier des charges, structurée comme le batch v1
 * (boucle quotidienne, clôtures hebdomadaire/mensuelle AVANT le jour J,
 * accumulateurs de semaine et de période). Les points où le CDC laisse une
 * micro-ambiguïté portent un marqueur `⚖ GM:` et sont consignés dans
 * docs/architecture/points-golden-master.md — chacun sera tranché par le
 * golden master (chap. 18) sans réécriture structurelle.
 *
 * SPDX-License-Identifier: GPL-3.0-only
 */
import { TypeRepos, estRegimeSansDemiRecup } from '@playtime/shared';
import { Commentaires } from './commentaires.js';
import { ajouterJours, comparerDates, dateInt, differenceJours, instant, jourSemaineIso, secondesAbsolues, semaineIso, } from './date-civile.js';
import { AGREGAT_VIDE, agregerPlanningParJour, agregerPrestationsParJour, sommeTypes1a7, } from './evenements.js';
import { formatHHMM, minutesVersHeures } from './format.js';
import { estFerie } from './jours-feries.js';
import { profilJour, } from './profil-jour.js';
import { MagasinRepos, echeanceRccte } from './repos.js';
// ─────────────────────────────────────────────────────────────────────────────
// RG-HIS-001 — résolution des attributs historisés « valeur au jour J »
// ─────────────────────────────────────────────────────────────────────────────
const FIN_OUVERTE = secondesAbsolues(instant({ an: 2099, mois: 12, jour: 31 }, 23, 59, 59));
/**
 * Sélection « valeur au jour J » : `DateDebut ≤ J+12:00 ≤ coalesce(DateFin,
 * 2099-12-31)` — le moteur interroge à MIDI pour éviter les effets de bord de
 * minuit (RG-HIS-001, à conserver). En cas de chevauchement (ne devrait pas
 * arriver, RG-HIS-002 clôturant à DébutNouveau − 1 s), la plage la plus
 * récemment insérée l'emporte. ⚖ GM-H1.
 */
export function valeurAuJour(plages, jour) {
    const midi = secondesAbsolues(instant(jour, 12, 0));
    let retenue;
    for (const p of plages) {
        const debut = secondesAbsolues(p.debut);
        const fin = p.fin ? secondesAbsolues(p.fin) : FIN_OUVERTE;
        if (debut <= midi && midi <= fin)
            retenue = p.valeur;
    }
    return retenue;
}
function attributsAuJour(c, jour) {
    // Défauts RG-PRF-001 : régime 0 / 'trimestre' ; nombre de jours/semaine 5.
    const regime = valeurAuJour(c.regimes, jour) ?? { idRegime: 0, echeanceRccte: 'trimestre' };
    // ⚖ GM-H2 : défaut du temps de travail absent du CDC — 38/38 retenu.
    const tdt = valeurAuJour(c.tempsDeTravail, jour) ?? {
        numerateur: 38,
        denominateur: 38,
        nombreDeJoursParSemaine: null,
    };
    const grille = valeurAuJour(c.regimesHoraires, jour) ?? null;
    return {
        attrs: {
            idRegime: regime.idRegime,
            estCdd: c.estCdd,
            premierJourContrat: c.premierJourContrat,
            dernierJourContrat: c.dernierJourContrat,
            tempsDeTravail: { numerateur: tdt.numerateur, denominateur: tdt.denominateur },
            nombreDeJoursParSemaine: tdt.nombreDeJoursParSemaine ?? 5,
            grilleHebdoMin: grille,
        },
        echeanceRccte: regime.echeanceRccte,
    };
}
function semaineVierge() {
    return {
        ts: 0,
        tssat: 0,
        as: 0,
        ps: 0,
        cssHeures: 0,
        sigmaHsq: 0,
        sigmaHcq: 0,
        heures11et12: 0,
        minutesDAffilee: 0,
        hafrHeures: 0,
        nbJoursPrestes: 0,
    };
}
function heuresAPayer(profil, etat, params) {
    const dS = etat.heuresSupTotales - etat.snapshotSup;
    const dC = etat.heuresCompTotales - etat.snapshotComp;
    let minutes;
    if (!profil.estPartiel || profil.estFlottant) {
        minutes = dS / 2; // RG-PAY-001 — sursalaire 50 % exprimé en heures
    }
    else {
        const palier = params.palierComplementaires100Min; // 12 h
        minutes =
            dC <= palier
                ? dC + dS / 2 // RG-PAY-002 — 12 premières complémentaires à 100 %
                : palier + (dC - palier) * 1.5 + dS / 2; // suivantes à 150 %, sup à 50 %
    }
    const texteHHMM = formatHHMM(minutes);
    const [h = 0, mm = 0] = texteHHMM.split(':').map(Number);
    return { minutes, texteHHMM, heuresDecimales: h + mm / 60 };
}
// ─────────────────────────────────────────────────────────────────────────────
// Boucle principale
// ─────────────────────────────────────────────────────────────────────────────
export function calculerContrat(contrat, periode, ctx, 
/** Repos hérités : magasin final de la période précédente recalculée dans ce run (historiques compris). */
reposHerites = [], 
/** Première période du run : les repos historiques de la base sont injectés une seule fois. */
premierePeriode = true) {
    const params = ctx.parametres;
    const horizon = ajouterJours(ctx.maintenant, -1); // RG-CAL-005
    const finContratPlusUn = contrat.dernierJourContrat
        ? ajouterJours(contrat.dernierJourContrat, 1)
        : null;
    // Bornes effectives (RG-PER-002)
    const debut = comparerDates(periode.debut, contrat.premierJourContrat) >= 0
        ? periode.debut
        : contrat.premierJourContrat;
    let fin = comparerDates(horizon, periode.fin) <= 0 ? horizon : periode.fin;
    if (finContratPlusUn && comparerDates(finContratPlusUn, fin) < 0)
        fin = finContratPlusUn;
    const vide = {
        lignes: [],
        repos: [],
        reposInternes: [],
        etatFinal: {
            aEteCalcule: false,
            dernierJourTraite: 0,
            limiteInterne: 0,
            heuresARecuperer: 0,
            heuresAPayer: 0,
        },
    };
    if (comparerDates(fin, debut) < 0)
        return vide;
    const estFerieJour = (j) => estFerie(j, ctx.feriesFlottants);
    const optionsAgregation = {
        estPlanifieDansFileMaker: contrat.estPlanifieDansFileMaker,
        estFerie: estFerieJour,
    };
    const planningParJour = agregerPlanningParJour(contrat.evenementsPlanning, optionsAgregation);
    const prestationParJour = agregerPrestationsParJour(contrat.evenementsPrestation, optionsAgregation);
    const dureeContratJours = contrat.dernierJourContrat
        ? differenceJours(contrat.premierJourContrat, contrat.dernierJourContrat) + 1
        : Number.POSITIVE_INFINITY;
    const magasin = new MagasinRepos();
    magasin.importer(reposHerites);
    if (premierePeriode)
        magasin.importer((contrat.reposHistoriques ?? []).map((h) => ({ ...h, historique: true })));
    const lignes = [];
    // CalculCompteurs.dpr L640, L695, L1652 : « Période de référence » = DateToStr(DateDebut)+'-'+DateToStr(DateFinPeriode)
    // avec DateDebut = max(début de période, premier jour de contrat) et DateFinPeriode = min(fin de période, lendemain du
    // dernier jour de contrat), au format court JJ-MM-AA du serveur v1 (données : « 11-09-23-19-09-23 »).
    const jjmmaa = (d) => `${String(d.jour).padStart(2, '0')}-${String(d.mois).padStart(2, '0')}-${String(d.an % 100).padStart(2, '0')}`;
    const debutContratPeriode = comparerDates(contrat.premierJourContrat, periode.debut) > 0 ? contrat.premierJourContrat : periode.debut;
    const lendemainFin = contrat.dernierJourContrat ? ajouterJours(contrat.dernierJourContrat, 1) : null;
    // L685-695 : DateFinPeriode = IncDay(IncWeek(DateDebut, 52), −1) calculée sur le début AJUSTÉ AU CONTRAT, ramenée au
    // dimanche, puis bornée au lendemain du dernier jour — un contrat commencé le 01/05/2023 porte « …-28-04-24 » (1171).
    let finLibelle = ajouterJours(debutContratPeriode, 52 * 7 - 1);
    while (jourSemaineIso(finLibelle) !== 7)
        finLibelle = ajouterJours(finLibelle, -1);
    const finContratPeriode = lendemainFin && comparerDates(lendemainFin, finLibelle) < 0 ? lendemainFin : finLibelle;
    const periodeRefLibelle = `${jjmmaa(debutContratPeriode)}-${jjmmaa(finContratPeriode)}`;
    const debutPeriodeInt = dateInt(periode.debut);
    let sem = semaineVierge();
    // CalculCompteurs.dpr L654 : `limiteinterne := 0` à chaque appel — la limite interne repart de zéro à chaque
    // période ET à chaque contrat, y compris entre deux CDD consécutifs d'une même personne (vérifié sur les données :
    // aucun report observé). Une éventuelle continuité entre CDD devrait donc être ajoutée comme règle NOUVELLE.
    const etat = {
        limiteInterne: 0,
        heuresARecuperer: 0,
        heuresSupTotales: 0,
        heuresCompTotales: 0,
        snapshotSup: 0,
        snapshotComp: 0,
        balanceDemi: 0,
        joursDAffilee: 0,
        veillePrestee: false,
        joursTraites: 0,
        jourARecupererReporteDemi: 0,
        aEuSoldePeriodique: false,
    };
    const derniereLigne = () => lignes[lignes.length - 1];
    const commenterDerniereLigne = (texte) => {
        const l = derniereLigne();
        if (!l)
            return;
        l.commentaire = l.commentaire ? `${l.commentaire}<br>${texte}` : texte;
    };
    // Données produites par le batch v1 (tables Compteurs 2019-2024) : « Mois » = mois civil (janvier = 1) et
    // « Semaine » = numéro de semaine ISO sans décalage (janvier = 1, 2, …). Le source livré contient un
    // décalage +14 / +52 sous condition d'indice (L1655-1682) qui n'apparaît dans aucune ligne produite.
    const moisNormalise = (j) => j.mois;
    const semaineNormalisee = (j) => String(semaineIso(j));
    // ── Clôture hebdomadaire (RG-HEB) ──────────────────────────────────────────
    const cloturerSemaine = (j, profil) => {
        if (etat.joursTraites === 0) {
            // « toute première semaine » (RG-HEB-050) : clôture à vide du premier
            // jour de calcul — rien à clore, rien à cumuler.
            sem = semaineVierge();
            return;
        }
        const s38 = params.semaineReferenceMin;
        const hafsHeures = profil.minutesAFaireParSemaine / 60;
        const baseMin = (hafsHeures - sem.cssHeures) * 60; // (HAFS − CSS)×60
        const tsMoinsAs = sem.ts - sem.as;
        const tssatMoinsAs = sem.tssat - sem.as;
        // RG-REP-010 ⚖ — RCS au constat (régime éligible AU MOMENT du constat),
        // sur les seules minutes PRESTÉES de la semaine écoulée.
        if (params.regimesEligiblesRcs.includes(profil.idRegime)) {
            for (const seuil of params.seuilsRcs) {
                if (sem.minutesDAffilee > seuil.seuilMin) {
                    const demi = Math.round(seuil.jours * 2);
                    magasin.generer({
                        type: TypeRepos[seuil.type],
                        dateDeclenchante: dateInt(ajouterJours(j, -1)), // CalculCompteurs.dpr L1498 : veille du constat (dimanche)
                        jourSemaineLimite: 5,
                        demiJours: demi,
                        dateDebutValidite: dateInt(j), // lundi de constat
                        // L1504-1512 : `IncDay(IncWeek(DateDebut, 52), −1)` avec `DateDebut` **ajusté au contrat** (contrat 976,
                        // commencé le 31/10/2022 : échéance 29/10/2023) — pas la fin de la période de référence.
                        dateEcheance: dateInt(ajouterJours(debutContratPeriode, 52 * 7 - 1)),
                    });
                    commenterDerniereLigne(Commentaires.generationRepos(TypeRepos[seuil.type], demi));
                    break; // une seule ligne par semaine — le seuil le plus élevé
                }
            }
        }
        // RG-HEB-010/020/030/040 — heures complémentaires / supplémentaires hebdo
        let hch = 0;
        let hsh = 0;
        if (profil.estFixe && profil.estPartiel) {
            // RG-HEB-010 — temps partiel fixe non-NRT
            if (tsMoinsAs > sem.ps && tsMoinsAs > baseMin) {
                hch = sem.ts - Math.max(sem.ps, baseMin) - sem.as;
                if (baseMin + hch > s38)
                    hch = s38 - baseMin;
            }
            if (tsMoinsAs > s38)
                hsh = sem.ts - Math.max(sem.ps, s38) - sem.as;
        }
        else if (profil.estFixe) {
            // RG-HEB-020 — temps plein fixe
            if (tsMoinsAs > sem.ps && tsMoinsAs > s38)
                hsh = sem.ts - Math.max(sem.ps, s38) - sem.as;
        }
        else if (profil.estFlottant) {
            // RG-HEB-040 — flottants : seuil unique 45 h proratisé
            const seuil = params.seuilHebdoFlottantMin * profil.fractionRegime;
            if (tssatMoinsAs > seuil)
                hsh = sem.tssat - seuil - sem.as;
        }
        else if (tssatMoinsAs > sem.ps && tssatMoinsAs > baseMin) {
            // RG-HEB-030 — NRT, petite flex, personne de confiance (base TSSAT,
            // révision 03/09/2023 : neutralisation de l'alignement tournée)
            hsh = sem.tssat - Math.max(sem.ps, baseMin) - sem.as;
        }
        // RG-HEB-045 — agrégation mensuelle « le plus favorable »
        let sigmaHcq = sem.sigmaHcq;
        if (profil.estFixe && profil.estPartiel && sigmaHcq + sem.tssat > s38) {
            sigmaHcq = sigmaHcq + s38 - sem.tssat; // replafonnement
        }
        const compSemaine = Math.max(sigmaHcq, hch);
        etat.heuresSupTotales += Math.max(sem.sigmaHsq, hsh);
        if (profil.estFixe && profil.estPartiel)
            etat.heuresCompTotales += compSemaine;
        // RG-HEB-001 / Q16 — champ hebdo porté par la DERNIÈRE ligne existante
        // (le dernier jour de la semaine close — le dimanche en croisière).
        const porteuse = derniereLigne();
        if (porteuse)
            porteuse.heuresSupHebdomadaires = hsh;
        // RG-HEB-050 — cumuls de période, si (J < fin de calcul OU J = fin de période)
        const estFinPeriode = comparerDates(j, periode.fin) === 0;
        if (comparerDates(j, horizon) < 0 || estFinPeriode) {
            const hTs = minutesVersHeures(sem.ts, 1); // RG-FMT-002 (1 décimale)
            etat.limiteInterne += hTs - 38;
            // BASE = Σ cibles quotidiennes (HAFR), SAUF régime 6 ⇒ HAFS contractuel.
            const baseHeures = profil.idRegime === 6 ? hafsHeures : sem.hafrHeures;
            etat.heuresARecuperer += hTs - (baseHeures - sem.cssHeures) * 1.0 - compSemaine / 60;
            // RG-HEB-060 — 11ᵉ/12ᵉ heures (régime 6) : récupération à 200 %…
            if (params.regimesEligiblesOnzeDouze.includes(profil.idRegime)) {
                etat.heuresARecuperer += sem.heures11et12 / 60;
            }
        }
        // …et JAMAIS de sursalaire sur ces heures (appairé à RG-HEB-045). ⚖ GM-H3
        if (params.regimesEligiblesOnzeDouze.includes(profil.idRegime)) {
            etat.heuresSupTotales -= sem.heures11et12;
        }
        sem = semaineVierge(); // RG-HEB-070
    };
    // ── Clôture mensuelle (RG-MEN) ─────────────────────────────────────────────
    const cloturerMois = (j, profil, echeance) => {
        // RG-MEN-001 — solde de balance → RCCTE (solde négatif : remis à zéro, Q14)
        if (etat.balanceDemi > 0) {
            magasin.generer({
                type: TypeRepos.RCCTE,
                dateDeclenchante: dateInt(ajouterJours(j, -1)), // L1007 : veille du 1er du mois
                jourSemaineLimite: 7,
                demiJours: etat.balanceDemi,
                dateDebutValidite: dateInt(j), // 1ᵉʳ du mois
                dateEcheance: echeanceRccte(ajouterJours(j, -1), echeance, periode.fin), // RG-MEN-003
            });
            commenterDerniereLigne(Commentaires.rccteMensuel(etat.balanceDemi));
        }
        etat.balanceDemi = 0;
        // RG-MEN-004 — ligne SOLDE PERIODIQUE : la paie du mois écoulé est le
        // DELTA par rapport aux snapshots (RG-MEN-002) — calculée AVANT leur mise
        // à jour ; Mois et Date repris de la dernière ligne journalière.
        const pay = heuresAPayer(profil, etat, params);
        const ref = derniereLigne();
        if (ref) {
            etat.aEuSoldePeriodique = true;
            lignes.push({
                idUtilisateur: contrat.idUtilisateur,
                periodeReference: periodeRefLibelle,
                mois: ref.mois,
                semaine: 'SOLDE PERIODIQUE',
                date: ref.date,
                heuresPlanifiees: '',
                heuresReellementPrestees: '',
                heuresAssimilees: '',
                heuresTotalisees: '',
                limiteInterne: ref.limiteInterne, // ⚖ GM-H4 — « reprenant de la dernière ligne »
                heuresARecuperer: ref.heuresARecuperer,
                commentaire: '',
                heuresSupQuotidiennes: 0,
                heuresSupHebdomadaires: 0,
                heuresSupplementaires: pay.texteHHMM,
                reposCompensatoiresEchusAPayer: magasin.demiJoursEchusAPayer(dateInt(j)) / 2,
            });
        }
        // RG-MEN-002 — snapshots (les cumuls ne sont JAMAIS remis à zéro)
        etat.snapshotSup = etat.heuresSupTotales;
        etat.snapshotComp = etat.heuresCompTotales;
    };
    // ── Boucle quotidienne ─────────────────────────────────────────────────────
    let j = debut;
    let profilDernier = null;
    while (comparerDates(j, fin) <= 0) {
        const { attrs, echeanceRccte: modeEcheance } = attributsAuJour(contrat, j);
        const profil = profilJour(attrs, j);
        const iso = jourSemaineIso(j);
        // CalculCompteurs.dpr : clôture mensuelle (L996-1052, ligne SOLDE PERIODIQUE) AVANT la clôture hebdomadaire (L1105+) —
        // un dimanche 30 qui est aussi fin de semaine porte le commentaire RCS sur la ligne SOLDE PERIODIQUE (contrat 53, 30/04/2023).
        // 1) Clôture mensuelle le 1ᵉʳ, hors premier jour de calcul (RG-MEN)
        if (j.jour === 1 && etat.joursTraites > 0)
            cloturerMois(j, profil, modeEcheance);
        // 2) Clôture hebdomadaire AVANT la ligne du jour (RG-HEB-001)
        const declencheCloture = iso === 1 ||
            comparerDates(j, fin) === 0 ||
            comparerDates(j, periode.fin) === 0 ||
            (finContratPlusUn !== null && comparerDates(j, finContratPlusUn) === 0);
        if (declencheCloture)
            cloturerSemaine(j, profil);
        // 3) Traitement du jour
        traiterJour(j, iso, profil, attrs);
        etat.joursTraites += 1;
        profilDernier = profil;
        j = ajouterJours(j, 1);
    }
    // ── Clôture finale (RG-FIN, RG-PAY) ────────────────────────────────────────
    const ref = derniereLigne();
    if (ref && profilDernier) {
        const pay = heuresAPayer(profilDernier, etat, params);
        const dernierJourTraite = ref.date;
        lignes.push({
            idUtilisateur: contrat.idUtilisateur,
            // Tables v1 : la ligne DERNIER SOLDE porte le libellé de période pour un contrat en cours et une période vide
            // pour un contrat terminé dans la période (24 contrats de N : 21 libellés, 3 vides = les 3 CDD terminés) ;
            // Mois NULL (assignation commentée L3903), 0 tient lieu de NULL.
            // L1058/L3901 : `tempPeriodeDeReference` n'est alimenté qu'à la création d'un SOLDE PERIODIQUE ; un contrat qui ne
            // franchit aucune fin de mois (1264, 1278, 1279) a donc une période vide sur son DERNIER SOLDE.
            periodeReference: etat.aEuSoldePeriodique ? periodeRefLibelle : '',
            mois: 0,
            semaine: 'DERNIER SOLDE',
            date: dernierJourTraite, // RG-FIN-001 — dernier jour traité
            heuresPlanifiees: '',
            heuresReellementPrestees: '',
            heuresAssimilees: '',
            heuresTotalisees: '',
            limiteInterne: etat.limiteInterne, // cumuls FINAUX (RG-FIN-001)
            heuresARecuperer: etat.heuresARecuperer,
            commentaire: '',
            heuresSupQuotidiennes: 0,
            heuresSupHebdomadaires: 0,
            heuresSupplementaires: pay.texteHHMM,
            reposCompensatoiresEchusAPayer: magasin.demiJoursEchusAPayer(dernierJourTraite) / 2,
        });
        // RG-FIN-004 — suppression du jour fictif de débordement (lendemain du
        // dernier jour de contrat), APRÈS insertion du DERNIER SOLDE.
        if (finContratPlusUn && comparerDates(fin, finContratPlusUn) === 0) {
            lignes.splice(lignes.length - 2, 1);
        }
        return {
            lignes,
            repos: magasin.exporter(contrat.idUtilisateur),
            reposInternes: magasin.internes(),
            etatFinal: {
                aEteCalcule: true,
                dernierJourTraite,
                limiteInterne: etat.limiteInterne,
                heuresARecuperer: etat.heuresARecuperer,
                heuresAPayer: pay.heuresDecimales,
            },
        };
    }
    return vide;
    // ───────────────────────────────────────────────────────────────────────────
    // Traitement d'UN jour
    // ───────────────────────────────────────────────────────────────────────────
    function traiterJour(jour, iso, profil, attrs) {
        const di = dateInt(jour);
        const agP = planningParJour.get(di) ?? AGREGAT_VIDE;
        const agR = prestationParJour.get(di) ?? AGREGAT_VIDE;
        const ferieJour = estFerieJour(jour);
        const ferieEnSemaine = ferieJour && iso <= 5; // RG-FER-003
        const plancher = profil.plancherMin;
        const hafMin = profil.minutesAFaireCeJour;
        const commentaires = [];
        // ── RG-PLA — heures planifiées du jour ───────────────────────────────────
        const planTravailBrut = agP.parType[1] + agP.relacheMin; // relâche incluse côté planning
        const sommePlan = sommeTypes1a7(agP, true);
        let planTravail = planTravailBrut;
        if (sommePlan > 0 && sommePlan < plancher)
            planTravail += plancher - sommePlan; // RG-PLA-001
        const ajoutsPlan = [];
        if (agP.parType[2] > 0)
            ajoutsPlan.push({ minutes: agP.parType[2], libelle: 'congé' });
        if (agP.parType[7] > 0)
            ajoutsPlan.push({ minutes: agP.parType[7], libelle: 'chômage temporaire' });
        if (agP.parType[3] > 0)
            ajoutsPlan.push({ minutes: agP.parType[3], libelle: 'maladie' });
        if (agP.parType[4] > 0)
            ajoutsPlan.push({ minutes: agP.parType[4], libelle: 'petit chômage' });
        let totalPlanifie = planTravail + ajoutsPlan.reduce((s, a) => s + a.minutes, 0); // RG-PLA-002
        // RG-PLA-003 — congé/récup planifié (types 5, 6 + 2, 7, 4)
        let congeOuRecupPlanifie = agP.parType[5] + agP.parType[6] + agP.parType[2] + agP.parType[7] + agP.parType[4];
        // ⚖ GM-H5 — complétion flottante (RG-PRF-004) appliquée au planifié total,
        // avant la règle férié.
        // CalculCompteurs.dpr L1826-1828 : `if (EstFlottant or EstFixe) … if (totalplanifie > 0) and (totalplanifie < Round(HAF*60)) then totalplanifie := Trunc(HAF*60)`
        // Chaque relèvement RÉÉCRIT la colonne « Heures planifiées » en une seule valeur (L1828, L1953, L1962) : le détail
        // « (congé) » disparaît (contrat 23 : « 07:36 »).
        let planifieReecrit = false;
        if ((profil.estFlottant || profil.estFixe) && totalPlanifie > 0 && totalPlanifie < hafMin) {
            totalPlanifie = Math.trunc(hafMin);
            planifieReecrit = true;
        }
        // RG-PLA-004 — férié en semaine planifié d'office
        if (ferieEnSemaine && totalPlanifie < hafMin) {
            totalPlanifie = Math.round(hafMin);
            planifieReecrit = true;
        }
        // RG-PLA-005 — normalisation 450 → 456 (L1960-1962)
        if (totalPlanifie === params.normalisationSourceMin) {
            totalPlanifie = params.normalisationCibleMin;
            planifieReecrit = true;
        }
        // ── RG-PST — heures prestées du jour ─────────────────────────────────────
        const sommePrest = sommeTypes1a7(agR, false); // la relâche n'entre pas (RG-PST-001)
        let totalPreste = agR.parType[1];
        if (sommePrest > 0 && sommePrest < plancher)
            totalPreste += plancher - sommePrest;
        const prestationJour = totalPreste > 0;
        if (prestationJour)
            sem.nbJoursPrestes += 1; // RG-PST-002
        // ── RG-REP-040 — 10 jours d'affilée ──────────────────────────────────────
        if (prestationJour) {
            etat.joursDAffilee = etat.veillePrestee ? etat.joursDAffilee + 1 : 1;
            if (etat.joursDAffilee > params.joursConsecutifsMax - 1) {
                magasin.generer({
                    type: TypeRepos.INACT10,
                    dateDeclenchante: di,
                    jourSemaineLimite: 7,
                    demiJours: 2,
                    dateDebutValidite: dateInt(ajouterJours(jour, 1)),
                    dateEcheance: dateInt(ajouterJours(jour, 1)), // le 11ᵉ jour doit être chômé
                });
                commentaires.push(Commentaires.generationRepos(TypeRepos.INACT10, 2));
                etat.joursDAffilee = 0;
            }
        }
        // ── RG-REP-001 — jour dû (balance) ───────────────────────────────────────
        if (prestationJour &&
            dureeContratJours > 5 &&
            (iso === profil.sixiemeJour || iso === profil.septiemeJour) &&
            (attrs.nombreDeJoursParSemaine < 6 || sem.nbJoursPrestes > 6)) {
            etat.balanceDemi += 2;
            commentaires.push(Commentaires.jourDu(etat.balanceDemi));
        }
        // ── RG-REP-020 — dimanche presté (jour 7 ABSOLU) ─────────────────────────
        if (prestationJour && iso === 7) {
            magasin.generer({
                type: TypeRepos.INACT15,
                dateDeclenchante: di,
                jourSemaineLimite: 6,
                demiJours: 2,
                dateDebutValidite: dateInt(ajouterJours(jour, 1)),
                dateEcheance: dateInt(ajouterJours(jour, params.fenetreInactiviteDimancheJours)),
            });
            commentaires.push(Commentaires.generationRepos(TypeRepos.INACT15, 2));
        }
        // ── RG-REP-030 — férié en semaine presté ─────────────────────────────────
        if (prestationJour && ferieEnSemaine) {
            congeOuRecupPlanifie += hafMin; // neutralise la règle maladie du jour
            magasin.generer({
                type: TypeRepos.RCF,
                dateDeclenchante: di,
                jourSemaineLimite: 5,
                demiJours: 2,
                dateDebutValidite: dateInt(ajouterJours(jour, 1)),
                dateEcheance: dateInt(ajouterJours(jour, params.fenetreReposFerieJours)),
            });
            commentaires.push(Commentaires.generationRepos(TypeRepos.RCF, 2));
        }
        // ── RG-ASS — heures assimilées du jour ───────────────────────────────────
        const plafond = profil.estFixe || profil.estFlottant ? Math.round(hafMin) : 456;
        const nrtLike = profil.estNrt || profil.estPetiteFlex || profil.estPersonneDeConfiance;
        let assimile = 0;
        let alignementTournee = 0;
        if (agR.aRelache)
            assimile += params.journeeReferenceMin; // RG-ASS-010 (sans plafond additionnel)
        assimile += Math.min(agR.parType[2], plafond); // RG-ASS-020 — congé
        assimile += Math.min(agR.parType[7], plafond); // RG-ASS-030 — chômage temporaire
        // RG-ASS-040 — maladie (trois cas selon CongéOuRécupPlanifié)
        const maladie = agR.parType[3];
        if (maladie > 0) {
            if (congeOuRecupPlanifie === 0) {
                assimile += Math.min(maladie, plafond);
            }
            else if (congeOuRecupPlanifie < plancher) {
                if (maladie <= plancher) {
                    const capCas2 = nrtLike ? plancher : Math.round(hafMin); // ⚖ GM-H6
                    assimile += Math.min(maladie, capCas2);
                }
                else {
                    assimile += Math.min(maladie, plafond);
                }
            } // ≥ plancher ⇒ non assimilée
        }
        assimile += Math.min(agR.parType[4], plafond); // RG-ASS-050 — petit chômage
        // RG-ASS-051 — congé sans solde : JAMAIS assimilé ; alimente CSS (prestations seules, Q5)
        if (agR.parType[5] > 0) {
            const css = agR.parType[5] === params.normalisationSourceMin
                ? params.normalisationCibleMin
                : agR.parType[5];
            sem.cssHeures += css / 60;
        }
        // RG-ASS-052 / RG-REC-020 — repos presté (type 6)
        if (agR.parType[6] > 0) {
            assimile += Math.min(agR.parType[6], plafond);
            const demiJours = agR.parType[6] <= plancher ? 1 : 2;
            // L2510-2512 : un seul commentaire « récupération ja RCS » avec le total consommé (ja cumulé sur les passes).
            const consommations = magasin.consommerReposEncode(profil.estNrt, demiJours, di);
            const total = consommations.reduce((s, x) => s + x.demiJours, 0);
            if (total > 0)
                commentaires.push(Commentaires.recuperationReposEncode(profil.estNrt ? TypeRepos.RCS45 : TypeRepos.RCCTE, total));
        }
        // RG-ASS-060 — férié en semaine, sans assimilation d'encodage préalable
        if (ferieEnSemaine && assimile === 0) {
            const hafArrondi = Math.round(hafMin);
            if (totalPreste === 0) {
                assimile =
                    profil.estNrt || profil.estPersonneDeConfiance ? params.journeeReferenceMin : hafArrondi;
            }
            else if (totalPreste <= plancher) {
                assimile = plancher - totalPreste;
            }
            else if (totalPreste <= hafArrondi) {
                assimile = hafArrondi - totalPreste;
            }
        }
        // RG-ASS-080 — complétion plancher du presté
        if (totalPreste > 0 && totalPreste < plancher && assimile < plancher - totalPreste) {
            assimile = plancher - totalPreste;
        }
        const recup = consommerRecuperations(jour, di, iso, profil, ferieJour, {
            totalPlanifie,
            totalPreste,
            assimile,
            plancher,
            hafMin,
            commentaires,
        });
        assimile = recup.assimile;
        // CalculCompteurs.dpr : alignement tournée (L3648-3660) et normalisation 450→456 (L3664-3669) sont exécutés APRÈS
        // les récupérations et le « jour pris » (L2755-3260) — un jour presté 04:00 en tournée reste à assimilé 0 au moment
        // du jour pris (contrats 40 et 53 : « ajoute un demi-jour pris »), puis est aligné à 07:36.
        // RG-ASS-070 — alignement tournée (toute journée de tournée vaut 7 h 36) — CalculCompteurs.dpr L3648-3660
        if (agR.tourneeMin > 0 && totalPreste + assimile < params.journeeReferenceMin) {
            alignementTournee = params.journeeReferenceMin - (totalPreste + assimile);
            assimile = params.journeeReferenceMin - totalPreste;
        }
        // RG-TMP-040 (b) — presté + assimilé = 450 ⇒ presté += 6, APRÈS l'alignement tournée (L3664-3666, GM-H7 arbitré).
        // La colonne « Heures réellement prestées » n'est pas réécrite en v1 (L3668-3669 ne réécrivent qu'assimilées et
        // totalisées) : elle affiche le presté AVANT normalisation (données : 07:30 / totalisées 07:36).
        const presteAffiche = totalPreste;
        if (totalPreste + assimile === params.normalisationSourceMin) {
            totalPreste += params.normalisationCibleMin - params.normalisationSourceMin;
        }
        // RG-TMP-040 (c)
        if (totalPreste + assimile === params.normalisationSourceMin) {
            totalPreste += params.normalisationCibleMin - params.normalisationSourceMin;
        }
        // ── RG-REC — récupérations ───────────────────────────────────────────────
        // ── RG-JRS — heures sup / complémentaires quotidiennes (presté SEUL) ─────
        let hsqJour = 0;
        const seuil9h = params.seuilSupQuotidienMin;
        if (totalPreste > seuil9h && totalPreste > totalPlanifie) {
            hsqJour = totalPlanifie > seuil9h ? totalPreste - totalPlanifie : totalPreste - seuil9h;
            sem.sigmaHsq += hsqJour; // RG-JRS-001
        }
        else if (profil.estFixe &&
            profil.estPartiel &&
            totalPreste <= seuil9h &&
            totalPreste > totalPlanifie) {
            // RG-JRS-002 — complémentaires quotidiennes + bascule 38 h
            sem.sigmaHcq += totalPreste - totalPlanifie;
            if (sem.sigmaHcq + totalPreste > params.semaineReferenceMin) {
                sem.sigmaHsq += sem.sigmaHcq + totalPreste - params.semaineReferenceMin;
                sem.sigmaHcq = params.semaineReferenceMin - totalPreste;
            }
        }
        // RG-JRS-003 — 11ᵉ/12ᵉ heures (régime 6)
        if (params.regimesEligiblesOnzeDouze.includes(profil.idRegime) &&
            totalPlanifie > params.seuilOnzeDouzeMin &&
            totalPreste > params.seuilOnzeDouzeMin) {
            // L3806-3822 : `heures11et12 := …` est une AFFECTATION, pas un cumul — sur une semaine comptant plusieurs
            // journées de plus de 10 h, seule la dernière compte (contrat 71, semaine 42 de 2022 : 4 journées de 10 h 30).
            sem.heures11et12 = Math.min(totalPreste, totalPlanifie) - params.seuilOnzeDouzeMin;
        }
        // ── Cumuls hebdomadaires ─────────────────────────────────────────────────
        sem.ts += totalPreste + assimile;
        sem.tssat += totalPreste + assimile - alignementTournee;
        sem.as += assimile;
        sem.ps += totalPlanifie;
        sem.minutesDAffilee += presteAffiche; // L2071 : `preste` de la requête, AVANT la normalisation 450→456 (RG-REP-010)
        sem.hafrHeures += hafMin / 60;
        // ── Ligne journalière ────────────────────────────────────────────────────
        const partTravail = totalPlanifie - ajoutsPlan.reduce((s, a) => s + a.minutes, 0);
        // L1705-1750 : lignes de texte dans l'ordre v1 — congé, chômage temporaire, maladie, petit chômage, puis, sans effet
        // sur le total planifié, congé sans solde (type 5) et repos compensatoire (type 6).
        const textesSeuls = [];
        if (agP.parType[5] > 0)
            textesSeuls.push({ minutes: agP.parType[5], libelle: 'congé sans solde' });
        if (agP.parType[6] > 0)
            textesSeuls.push({ minutes: agP.parType[6], libelle: 'repos compensatoire' });
        const detailPlan = planifieReecrit
            ? formatHHMM(totalPlanifie)
            : [formatHHMM(partTravail)]
                .concat(ajoutsPlan.concat(textesSeuls).map((a) => `${formatHHMM(a.minutes)} (${a.libelle})`))
                .join('<br>');
        lignes.push({
            idUtilisateur: contrat.idUtilisateur,
            periodeReference: periodeRefLibelle,
            mois: moisNormalise(jour),
            semaine: semaineNormalisee(jour),
            date: di,
            heuresPlanifiees: detailPlan,
            heuresReellementPrestees: formatHHMM(presteAffiche),
            heuresAssimilees: formatHHMM(assimile),
            heuresTotalisees: formatHHMM(totalPreste + assimile),
            limiteInterne: etat.limiteInterne,
            heuresARecuperer: etat.heuresARecuperer,
            commentaire: commentaires.join('<br>'),
            heuresSupQuotidiennes: hsqJour,
            heuresSupHebdomadaires: 0, // posé a posteriori par la clôture (RG-HEB-001)
            // L3719-3745 : chaque ligne journalière porte le texte « heures à payer » courant (mêmes formules que le solde).
            heuresSupplementaires: heuresAPayer(profil, etat, params).texteHHMM,
            // CalculCompteurs.dpr L1086-1103 : la somme des repos RCS/RCCTE échus (échéance ≤ jour) est écrite sur CHAQUE ligne.
            reposCompensatoiresEchusAPayer: magasin.demiJoursEchusAPayer(dateInt(jour)) / 2,
        });
        etat.veillePrestee = prestationJour;
    }
    // ───────────────────────────────────────────────────────────────────────────
    // RG-REC-001/002/003/004/005/006 — consommation des repos et récupérations
    // ───────────────────────────────────────────────────────────────────────────
    function consommerRecuperations(jour, di, iso, profil, ferieJour, valeurs) {
        const { totalPlanifie, totalPreste, plancher, hafMin, commentaires } = valeurs;
        let assimile = valeurs.assimile;
        const nrtLike = profil.estNrt || profil.estPetiteFlex || profil.estPersonneDeConfiance;
        const enWeekendRelatif = iso === profil.sixiemeJour || iso === profil.septiemeJour;
        const weekendRelatifVideEntree = enWeekendRelatif && totalPlanifie === 0 && totalPreste === 0 && !ferieJour;
        // L2712-2740 : un férié en semaine est traité dans la branche « férié » (assimilation, RCF) ; la détermination du
        // jour à récupérer, les consommations et le « jour pris » sont dans la branche « pas un jour férié » (L2741+).
        if (ferieJour && iso <= 5)
            return { assimile };
        // ── Porte d'entrée : jourÀRécupérer (demi-jours) — `jourarecuperer` v1 ────
        // L2757-2775 : la valeur n'est (re)calculée que si le jour est « faible » ; sinon elle est
        // conservée de la veille (aucune réinitialisation quotidienne). Sur le 6ᵉ/7ᵉ jour relatif vide
        // (L2634), c'est la valeur héritée qui décide de la consommation d'une inactivité.
        // Périmètre reproduit à ce stade : l'héritage sur le 6ᵉ/7ᵉ jour relatif vide (contrats 1252, 336 : 0 écart).
        // L'héritage sur les autres jours non « faibles » (jours prestés, fériés) n'est PAS encore reproduit : son
        // application brute crée des « jour pris » les fériés que la v1 n'a pas (contrat 1171, 01/11/2023) — à tracer.
        let restantDemi = 0;
        if (weekendRelatifVideEntree) {
            restantDemi = etat.jourARecupererReporteDemi;
        }
        else if (assimile === 0 &&
            ((totalPreste === 0 && (nrtLike || hafMin > 0)) ||
                (totalPreste > 0 && totalPreste <= plancher && (nrtLike || hafMin > 240)))) {
            // RG-REC-002 — jour faible (L2757-2775)
            if (totalPreste === 0)
                restantDemi = hafMin <= 240 ? 1 : 2;
            else
                restantDemi = estRegimeSansDemiRecup(profil.idRegime) ? 0 : 1; // L2769-2775 : régimes {0,2,3,8} → 0, autres (1,4,5,6,7) → 0,5
            etat.jourARecupererReporteDemi = restantDemi;
        }
        if (restantDemi <= 0)
            return { assimile };
        const memoriser = () => { etat.jourARecupererReporteDemi = Math.max(0, restantDemi); };
        // ── RG-REC-006 — garde-fou « jour pris » (max 1 j/jour, toutes règles) ──
        let jourPrenableDemi = 2; // `jourprenable := 1` réinitialisé chaque jour (L956)
        // L3186-3210 (et L2665-2700, L2825-2849, L2904-2928) : jour pris si jourprenable ≥ 1, presté = 0, (nrtLike ou HAF > 0) ;
        // demi-jour pris si jourprenable ≥ 0,5, presté ≤ durée minimale, (nrtLike ou HAF > 4 h), régime ∉ {0,2,3,8}.
        // Le bloc « jour pris » v1 n'est évalué qu'une fois par jour (pas de seconde passe) : un seul décrément possible.
        let jourPrisEvalue = false;
        const tenterJourPris = () => {
            if (jourPrisEvalue)
                return;
            jourPrisEvalue = true;
            if (jourPrenableDemi >= 2 && totalPreste === 0 && (nrtLike || hafMin > 0)) {
                etat.balanceDemi -= 2;
                jourPrenableDemi -= 2;
                commentaires.push(Commentaires.jourPris(etat.balanceDemi));
            }
            else if (jourPrenableDemi >= 1 &&
                totalPreste <= plancher &&
                (nrtLike || hafMin > 240) &&
                !estRegimeSansDemiRecup(profil.idRegime)) {
                etat.balanceDemi -= 1;
                jourPrenableDemi -= 1;
                commentaires.push(Commentaires.demiJourPris(etat.balanceDemi));
            }
        };
        let inactiviteConsommee = false; // les deux inactivités sont exclusives le même jour
        // CalculCompteurs.dpr L2634-2712 : sur le 6ᵉ/7ᵉ jour relatif sans planifié ni presté (hors férié), SEULE une
        // inactivité (15 j / 10 j) peut être consommée ; pas de RCCTE/RCF, pas de « jour pris » hors inactivité.
        // Contrat 1252 (début mercredi ⇒ 6ᵉ/7ᵉ = lundi/mardi) : aucune récupération les 02-03/10/2023, reprise le 04/10.
        const weekendRelatifVide = enWeekendRelatif && totalPlanifie === 0 && totalPreste === 0 && !ferieJour;
        const passe = () => {
            // Temps 1 — INACT15 (J ≠ dimanche/7ᵉ jour, non férié, presté = 0,
            // assimilé = 0, aucune inactivité déjà consommée ce jour) — NON
            // assimilée, sans contrainte d'échéance (Q8). Sur le week-end relatif
            // vide, la v1 ne pose pas la condition 7ᵉ jour (filtre L2636-2642).
            if (!inactiviteConsommee &&
                (weekendRelatifVide || (iso !== 7 && iso !== profil.septiemeJour)) &&
                !ferieJour &&
                totalPreste === 0 &&
                assimile === 0 &&
                restantDemi > 0) {
                const r = magasin.premiereInactiviteDisponible(TypeRepos.INACT15, di, iso);
                // L2799-2801 : `R.First` puis `if [Solde] >= jourarecuperer` — un SEUL enregistrement est examiné. Un reliquat
                // (solde 0,5) en tête de file bloque définitivement la consommation quand le jour à récupérer vaut 1 j
                // (contrat 42 : inactivité du 26/06/2022 à 0,5, plus aucune consommation ensuite).
                if (r && r.soldeDemiJours >= restantDemi) {
                    const pris = magasin.consommer(r, restantDemi, di);
                    restantDemi = 0; // L2817 / L2665 : `jourarecuperer := 0`
                    void pris;
                    inactiviteConsommee = true;
                    commentaires.push(Commentaires.consommationInactivite(TypeRepos.INACT15, pris));
                    if (iso !== profil.septiemeJour)
                        tenterJourPris();
                    return;
                }
            }
            // Temps 2 — INACT10 (tout jour, presté = 0, assimilé = 0)
            if (!inactiviteConsommee && totalPreste === 0 && assimile === 0 && restantDemi > 0) {
                const r = magasin.premiereInactiviteDisponible(TypeRepos.INACT10, di, iso);
                if (r && r.soldeDemiJours >= restantDemi) { // L2876-2878 : même garde `[Solde] >= jourarecuperer`
                    const pris = magasin.consommer(r, restantDemi, di);
                    restantDemi = 0; // L2894
                    void pris;
                    inactiviteConsommee = true;
                    commentaires.push(Commentaires.consommationInactivite(TypeRepos.INACT10, pris));
                    // L2905-2925 : après une inactivité 10 j, le « jour pris » suit SANS condition de jour (contrat 71, dimanche 27/11/2022).
                    tenterJourPris();
                    return;
                }
            }
            // Temps 3 — pool fusionné RCF + RCCTE (seulement si AUCUNE inactivité
            // consommée ce jour, et jamais sur un week-end relatif vide — L2634-2712) :
            // SEUL le premier enregistrement (échéance croissante) est examiné (RG-REC-003 ⚖).
            if (!inactiviteConsommee && !weekendRelatifVide) {
                const r = magasin.premierDuPoolRcfRccte(di, iso);
                if (r) {
                    let pris = 0;
                    if (iso <= 5 && restantDemi > 0) {
                        pris = magasin.consommer(r, restantDemi, di);
                        restantDemi -= pris;
                        // Assimilation : 1 j ⇒ += HAF×60 ; 0,5 j ⇒ += plancher − presté,
                        // normalisation 480 → 456 (Q20 — la seconde passe RÉ-assimile).
                        // L3060-3068 : 1 j ⇒ += HAF×60 ; 0,5 j ⇒ += durée minimale − presté, plancher à la durée minimale,
                        // 480 → 456 ; puis plancher à la durée minimale dans les deux cas.
                        if (pris === 2)
                            assimile += Math.round(hafMin);
                        else if (pris === 1) {
                            assimile += plancher - totalPreste;
                            if (assimile < plancher)
                                assimile = plancher;
                            if (assimile === 480)
                                assimile = 456;
                        }
                        if (assimile < plancher)
                            assimile = plancher;
                        commentaires.push(r.type === TypeRepos.RCCTE
                            ? Commentaires.recuperationRccte(pris)
                            : Commentaires.recuperationRcf(pris));
                    }
                    // L3062/L3131 : la récupération consomme `jourprenable` (− ja).
                    if (pris > 0)
                        jourPrenableDemi -= pris;
                    return; // L3160-3178 : le « jour pris » est dans le `else` du pool — un repos trouvé l'exclut.
                }
                // L3178-3184 : « PAS ASSEZ DE RECUP => on déduit de la balance jours pris/jour dus » :
                // `jourarecuperer := 0`, puis jour pris / demi-jour pris (contrat > 5 jours, hors 6ᵉ/7ᵉ jour relatif).
                restantDemi = 0;
                if (iso !== profil.sixiemeJour &&
                    iso !== profil.septiemeJour &&
                    dureeContratJours > 5) {
                    tenterJourPris();
                }
                return;
            }
        };
        passe();
        if (restantDemi > 0)
            passe(); // ⚖ Q20 — seconde passe À L'IDENTIQUE
        memoriser();
        return { assimile };
    }
}
//# sourceMappingURL=calcul-contrat.js.map